import {
    AfterViewInit,
    ContentChildren,
    Directive,
    ElementRef,
    EventEmitter,
    Input,
    NgZone,
    OnDestroy,
    Output,
    QueryList,
    Renderer2
} from '@angular/core';
import { TimeoutService } from '@df/ng/timeout.service';
import { TypewriteEffectTextDirective } from '@df/ui/display/effect/typewrite-effect/typewrite-effect-text.directive';
import forEach from 'lodash-es/forEach';

enum ETypewriteDirection {
    back,
    forward
}

@Directive({
    selector: '[typewriteEffect]',
    standalone: true
})
export class TypewriteEffectDirective implements AfterViewInit, OnDestroy {
    private _disabled = false;
    @Input()
    set disabled(value: boolean) {
        if (this._disabled !== value) {
            this._disabled = value;

            this.pauseAnimation(this.pauseBeforeStart, true);
        }
    }

    @Input()
    pauseBeforeStart = 2000;

    @Input()
    pauseBetweenTexts = 250;

    @Input()
    pauseAfterText = 2000;

    @Input()
    pauseOnLastText = 4000;

    @Input()
    speedIn = 50;

    @Input()
    speedOut = 10;

    @Input()
    stopOnLast = false;

    /** Event emitted when is destroyed. */
    @Output()
    destroyed = new EventEmitter<void>();

    private _current = 0;
    private _letter = 0;
    private _interval?: any;
    private _timeout?: any;
    private _direction: ETypewriteDirection = ETypewriteDirection.forward;
    private _texts?: TypewriteEffectTextDirective[];

    @ContentChildren(TypewriteEffectTextDirective)
    protected set texts(value: QueryList<TypewriteEffectTextDirective>) {
        this._texts = value.toArray();

        this.pauseAnimation(this.pauseBeforeStart, true);
    }

    constructor(
        protected elementRef: ElementRef,
        protected renderer2: Renderer2,
        protected ngZone: NgZone,
        private timeoutService: TimeoutService
    ) {}

    ngOnInit(): void {
        this.renderer2.setStyle(this.elementRef.nativeElement, 'opacity', 0);
    }

    ngAfterViewInit(): void {
        if (this._texts) {
            this.processTexts();
        }
    }

    ngOnDestroy(): void {
        this.stopAnimation();

        this.destroyed.emit();
        this.destroyed.complete();
    }

    /** Check if it is last text */
    get isLastText(): boolean {
        return this._current + 1 === this._texts?.length;
    }

    /**
     * Hide text on init and then setup animation.
     */
    protected processTexts(): void {
        this._texts?.forEach((text: TypewriteEffectTextDirective, index: number) => {
            this.processLetters(text.elementRef.nativeElement.children);

            if (index + 1 === this._texts?.length) {
                this.renderer2.setStyle(this.elementRef.nativeElement, 'opacity', null);

                this.pauseAnimation(this.pauseBeforeStart);
            }
        });
    }

    protected processLetters(letters: HTMLElement[]): void {
        forEach(letters, (letter: HTMLElement) => {
            this.renderer2.setStyle(letter, 'opacity', 0);
        });
    }

    protected startAnimation(): void {
        if (!this._disabled) {
            this._interval = this.timeoutService.setInterval(() => this.tick(), this.getSpeed());
        }
    }

    /**
     * Pause animation and start after `timeOfRestart`
     */
    protected pauseAnimation(timeOfRestart = 0, reset = true): void {
        this.stopAnimation(reset);
        this._timeout = this.timeoutService.setTimeout(() => !this._disabled && this.startAnimation(), timeOfRestart);
    }

    /**
     * Stop animation
     */
    protected stopAnimation(reset = false): void {
        this.ngZone.runOutsideAngular(() => {
            clearInterval(this._interval);
            clearTimeout(this._timeout);
            this._interval = undefined;
            this._timeout = undefined;

            if (reset && this._texts) {
                this._current = 0;
                this._letter = 0;
                this._direction = ETypewriteDirection.forward;

                this._texts.forEach((text: TypewriteEffectTextDirective) => {
                    this.processLetters(text.elementRef.nativeElement.children);
                });
            }
        });
    }

    /**
     * Process animation
     */
    protected tick(): void {
        if (!this._texts?.length) {
            this.stopAnimation();
            return;
        }

        if (this._current !== undefined && this._texts[this._current]) {
            const text = this._texts[this._current].elementRef.nativeElement;
            const letters = text.children;

            if (this._letter + 1 > letters.length) {
                this._direction = ETypewriteDirection.back;
                this.pauseAnimation(this.isLastText ? this.pauseOnLastText : this.pauseAfterText, false);
            }

            if (this._direction === ETypewriteDirection.back) {
                if (letters[this._letter]) {
                    this.renderer2.setStyle(letters[this._letter], 'opacity', 0);
                }

                if (this.isLastText && this.stopOnLast) {
                    this.stopAnimation();
                } else if (this._letter > 0) {
                    this._letter -= 1;
                } else {
                    if (!this.isLastText) {
                        this._current += 1;
                    } else {
                        this._current = 0;
                    }

                    this._letter = 0;
                    this._direction = ETypewriteDirection.forward;
                    this.pauseAnimation(this.pauseBetweenTexts, false);
                }
            } else {
                if (letters[this._letter]) {
                    this.renderer2.setStyle(letters[this._letter], 'opacity', 1);
                }

                this._letter += 1;
            }
        } else {
            this.stopAnimation();
        }
    }

    /**
     * Get animation speed. If direction is `back` then speed half
     */
    protected getSpeed(): number {
        return this._direction === ETypewriteDirection.back ? this.speedOut : this.speedIn;
    }
}
